import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import useSWR from 'swr'
import { FileMagicNumber } from 'types/building'
import Utils from 'utils'

import useTourUploadImage, { TourUploadImagePath } from '../useTourUploadImage'

export default function useEraseTripod({
  handleCancel
}: {
  handleCancel: () => void
}) {
  const { t } = useTranslation()
  const { id: buildingId } = useParams()
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  // handle get  building details information
  const { data: buildingInfo, isLoading } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${buildingId}`
  })
  // const { trigger: handleGetImage } = useMutation('v1/points')
  const [form] = Form.useForm()
  // State for tripod image selected
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChooseFile = () => inputRef?.current?.click()

  const tripodImageWatch = Form.useWatch('tripodImage', form)

  const tripodImage = useMemo(() => {
    if (selectedFile) {
      return URL.createObjectURL(selectedFile)
    }
    return tripodImageWatch
  }, [selectedFile, tripodImageWatch])

  const isFormValid = tripodImageWatch

  const { trigger: updateTripod, isMutating: updating } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`
  )

  const { trigger: deleteLogo, isMutating: deleting } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`,
    {},
    true // state for pass empty string for update
  )
  const { trigger: downloadTripodImage } = useMutation(
    `${apiPrefix}/buildings/files/download`
  )
  /**
   * hook for upload file return folder of S3 (key)
   */
  const { uploadFile } = useTourUploadImage({
    buildingId: Number(buildingId),
    path: TourUploadImagePath.TRIPOD,
    selectedFile
  })

  /**
   * Handle save tripod image
   */
  const handleSaveTripod = async () => {
    if (!selectedFile && buildingInfo?.extraInfo?.tripodImageUrl) {
      // Force jpg extension
      const fileExtension = 'jpg'

      // Map file extension to mime type
      const mimeTypes: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp'
      }

      const mimeType = mimeTypes[fileExtension]

      const response = await downloadTripodImage({
        data: {
          s3_path: buildingInfo.extraInfo.tripodImage,
          building_id: buildingInfo.id
        },
        headers: {
          Accept: mimeType,
          'Content-Type': 'application/json'
        },
        responseType: 'blob',
        method: 'post'
      })

      // Check if response exists
      if (!response) {
        throw new Error('No response from server')
      }

      // The response itself might be the blob
      const blobData = response instanceof Blob ? response : response.data

      // Check if we have valid blob data
      if (!(blobData instanceof Blob) || blobData.size === 0) {
        throw new Error('Invalid image data received')
      }

      // Create a new blob with the correct mime type
      const blob = new Blob([blobData], { type: mimeType })

      if (blob.size === 0) {
        throw new Error('Generated blob is empty')
      }

      // Create and trigger download
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = url
      // set name of file downd load
      link.download = `tripod-${
        buildingInfo.id
      }-${buildingInfo.extraInfo.tripodImage
        ?.split('/')
        .pop()}.${fileExtension}`

      document.body.appendChild(link)
      link.click()

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 100)

      handleCancel() // Close the modal after download starts
    } else if (selectedFile) {
      await uploadFile({
        onSuccess: (key) => {
          updateTripod({
            method: 'patch',
            data: { tripodImage: key },
            successMessage: t('building.saved')
          }).then(() => handleCancel())
        }
      })
    }
  }

  /**
   * Handle delete tripod image
   */
  const handleDeleteTripod = () => {
    deleteLogo({
      method: 'patch',
      data: {
        tripodImage: ''
      },
      successMessage: t('building.deleted')
    }).then(() => {
      // closed modal when delete logo success
      handleCancel()
    })
  }
  /**
   * Handle process file change
   */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    const resetForm = () => {
      setSelectedFile(null)
      form.resetFields(['tripodImage'])
    }

    if (files?.length) {
      form.setFields([{ name: 'tripodImageWrapper', errors: [] }])

      Utils.validateFile(files[0], t)
        .then(() => {
          Utils.getFileMagicNumber(files[0], FileMagicNumber.IMAGE)
            .then(() => {
              setSelectedFile(files[0])
              form.setFieldValue('tripodImage', files[0])
              form.setFields([{ name: 'tripodImage', errors: [] }])
            })
            .catch(() => {
              form.setFields([
                {
                  name: 'tripodImageWrapper',
                  errors: [t('translation.brokenFileMessage')]
                },
                { name: 'tripodImage', value: '' }
              ])
              setSelectedFile(null)
            })
        })
        .catch((error: any) => {
          form.setFields([
            {
              name: 'tripodImageWrapper',
              errors: [error.message]
            },
            { name: 'tripodImage', value: '' }
          ])
          setSelectedFile(null)
          form.setFieldValue('tripodImage', undefined)
        })
      return
    }

    if (buildingInfo?.extraInfo?.tripodImageUrl) {
      // If there's an existing image URL, restore it instead of resetting
      setSelectedFile(null)
      form.setFieldValue('tripodImage', buildingInfo.extraInfo.tripodImageUrl)
    } else {
      resetForm()
    }
  }
  /**
   * set initial value of form
   */
  const initialFormValue = useMemo(() => {
    if (buildingInfo && buildingInfo.id === Number(buildingId)) {
      return {
        tripodImage: buildingInfo.extraInfo.tripodImageUrl
      }
    }
    return null
  }, [buildingInfo, buildingId])

  useEffect(() => {
    if (initialFormValue) {
      form.setFieldsValue(initialFormValue)
    }
  }, [form, initialFormValue])

  useEffect(() => {
    // Reset state when building changes to avoid showing old data
    setSelectedFile(null)
    form.resetFields()
  }, [buildingId, form])

  return {
    form,
    inputRef,
    tripodImage,
    isFormValid,
    selectedFile,
    buildingInfo,
    handleSaveTripod,
    handleChooseFile,
    handleFileChange,
    handleDeleteTripod,
    isLoading,
    updating,
    deleting,
    t
  }
}
